import axios, { type AxiosInstance } from 'axios';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: '/api',
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
      withCredentials: true,
    });
  }

  private getCsrfToken(): string | null {
    // 首先尝试从meta标签获取
    const metaToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (metaToken) return metaToken;

    // 如果meta标签没有，尝试从cookie获取
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return value;
      }
    }

    return null;
  }

  setupInterceptors() {
    this.api.interceptors.request.use(
      (config) => {
        const csrfToken = this.getCsrfToken();

        // Session 认证 - withCredentials 会在请求中自动包含 session cookie
        // 不需要手动添加 Authorization header

        if (csrfToken) {
          config.headers['X-CSRFToken'] = csrfToken;
        }

        if (!(config.data instanceof FormData)) {
          config.headers['Content-Type'] = 'application/json';
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: Parameters<AxiosInstance['get']>[1]): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data: unknown, config?: Parameters<AxiosInstance['post']>[2]): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data: unknown, config?: Parameters<AxiosInstance['put']>[2]): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data: unknown, config?: Parameters<AxiosInstance['patch']>[2]): Promise<T> {
    const response = await this.api.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: Parameters<AxiosInstance['delete']>[1]): Promise<T> {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }
}

const apiService = new ApiService();
// 初始化拦截器（必须在导出前调用）
apiService.setupInterceptors();
export default apiService;
